import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWindowSize } from '@navi-toolbox/hooks';
import { Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { cx } from 'linaria';
import { getWishlist, WishlistItemFlow } from 'features/wishlist';
import { connect, useSelector } from 'react-redux';
import { ConnectedProps } from 'types/utils';
import { getProductId } from 'features/product-info/ducks/selectors';
import { WISHLIST_ITEM_FLOW } from 'features/wishlist/ducks/actions';
import { useBreakpoints } from 'common/hooks';
import { v4 } from 'uuid';
import { IconButton, Snack, Snackbar, SnackbarProvider, Tooltip, useSnackbar } from 'fronton-react';
import { BookmarkSimpleIcon, CheckCircleIcon } from '@fronton/icons-react';
import { inListButton, inListButtonActive } from './styles';

type PdpWishlistCheckboxProps = ConnectedProps<typeof mapDispatchToProps> &
  React.HTMLAttributes<HTMLDivElement>;

const isInWishlist = (productId: string) =>
  createSelector(getWishlist, (wishlist) => wishlist?.some(({ code }) => code === productId)) || false;

const WishlistWithSnackProvider = ({ ...props }: PdpWishlistCheckboxProps) => (
  <SnackbarProvider>
    <WishlistCheckbox {...props} />
  </SnackbarProvider>
);

export const WishlistCheckbox: React.FC<PdpWishlistCheckboxProps> = ({ onWishlistChange, className }) => {
  const [mounted, setMounted] = useState(false);
  const breakpoints = useBreakpoints();
  const { width } = useWindowSize();
  const isDesktop = useMemo(() => breakpoints?.largeDesktop || breakpoints?.desktop, [breakpoints]);
  const { state, addSnack } = useSnackbar();
  const productId: string = useSelector(getProductId) || '';
  const [inWishlist, setInWishlist] = useState(useSelector(isInWishlist(productId)));

  const changeHandler = useCallback(() => {
    const nextValue = !inWishlist;
    setInWishlist(() => nextValue);
    onWishlistChange(productId, nextValue ? 1 : -1);
    if (nextValue) {
      addSnack({
        id: v4(),
        title: 'Товар добавлен в список покупок',
      });
    }
  }, [inWishlist, onWishlistChange, productId, addSnack]);

  const tipText = inWishlist ? 'Удалить из списка покупок' : 'Добавить в список покупок';

  const button = (
    <IconButton
      data-qa="wishlist-button"
      withExtraClickZone
      label={tipText}
      size="l"
      onClick={changeHandler}
      className={cx(className, inWishlist ? inListButtonActive : inListButton)}
    >
      {inWishlist ? (
        <BookmarkSimpleIcon data-qa="bookmark-icon-set" type="fill" size="l" color="brand-primary" />
      ) : (
        <BookmarkSimpleIcon data-qa="bookmark-icon-not-set" type="outline" size="l" color="text-primary" />
      )}
    </IconButton>
  );

  useEffect(() => {
    setMounted(true);
  }, [width]);

  if (!mounted) {
    return null;
  }

  return (
    <>
      {isDesktop ? (
        <Tooltip placement="top-end" title={tipText}>
          {button}
        </Tooltip>
      ) : (
        <>{button}</>
      )}
      <Snackbar verticalPosition="bottom">
        {state.map(({ id, title }) => (
          <Snack
            key={id}
            id={id}
            header={title}
            icon={<CheckCircleIcon type="outline" size="l" color="text-primary" />}
            variant="neutral"
            autoHideTimer={3000}
          />
        ))}
      </Snackbar>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onWishlistChange: (productId: WishlistItemFlow['productId'], flowQty: WishlistItemFlow['flowQty']) => {
    dispatch(WISHLIST_ITEM_FLOW({ productId, flowQty }));
  },
});

export const PdpWishlistCheckbox = connect(null, mapDispatchToProps)(WishlistWithSnackProvider);
